import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import axios from 'axios';

import { triggerPayoutRequest, retryFailedTransfersRequest, getUsersRequest, getPayoutStatusRequest, skipTransfersRequest, reclaimAndTransferRequest, reclaimToPlatformRequest, processHeldReversalsRequest, getEmployeeSalesRequest, getTipFundingSummaryRequest, isWorkPeriodMarkedPaidLocally, rememberWorkPeriodPaidLocally, getLocalManualPayReason, sendEmployeeSalesReportEmailRequest, sendEmployeeSalesReportSmsRequest } from '../../../actions/users';
import { loadStoreDetails } from '../../../actions/settings';
import { getWorkperiodPooledTipShareRequest, payWorkperiodPooledTipsRequest } from '../../../actions/sales';
import {
    formatTipPayoutStatusLabel,
    resolveManualPayReasonFromRow,
    resolveEffectiveTipPayoutStatus,
    resolveTipPayoutNoteFromRow,
    buildSplitPayoutStatusNotes,
    buildWorkPeriodPayoutAmountSplit,
    getRemainingTipPayoutAmount,
    isManagerRoutedPayReason,
    isPooledDeferredPayReason,
} from '../../../utils/tipPayoutStatus';
import { excludeCashTipTransfers } from '../../../utils/tipTransferCash';
import { hasRefundReporting, reportingNets, salesAmounts } from '../../../utils/employeeSalesAmounts';
import { withTranslation } from '../../../i18n/LanguageContext';
import {
    formatApiTime,
    formatApiDateLong,
    formatApiClockOut,
    calendarDateFromApi,
    parseApiDateTime,
} from '../../../utils/merchantLocalDateTime';
import { isRebelityPlatformAdmin } from '../../../actions/auth';
import { sendOnboardingLinkEmailRequest, sendOnboardingLinkSmsRequest } from '../../../actions/stripe';
import Loader from '../../../components/Loader';
import Widget from '../../../components/Widget';
import { toast } from 'react-toastify';
import s from './WorkperiodDetails.module.scss';

class WorkperiodDetails extends React.Component {
    static propTypes = {
        dispatch: PropTypes.func.isRequired,
    };

    state = {
        routeTransferStatus: null,
        routePayoutActions: null,
        sendReportLoading: null,
        pooledShare: null,
        pooledShareLoading: false,
        pooledPayLoading: false,
        // In-app confirm for pooled pay — window.confirm is suppressed (auto-OK)
        // in embedded browsers, which would move money with no confirmation.
        pooledPayConfirmMsg: null,
    };

    componentDidMount() {
        this._isMounted = true;

        if (this.props.merchantId && this.props.storeSettingsMerchantId !== this.props.merchantId) {
            this.props.dispatch(loadStoreDetails());
        }

        // If we don't have a full employee object (e.g. navigated from Sales),
        // load the users list so mapStateToProps can look up the employee
        const emp = this.props.selectedEmployee;
        if ((!emp || !emp.Email) && this.props.merchantId) {
            this.props.dispatch(getUsersRequest(this.props.merchantId));
        }

        // Always (re-)fetch payout status so hasStripeSetup() reflects the
        // employee's actual Stripe account state, even when landing here directly.
        const userId = parseInt(this.props.match?.params?.userId);
        if (userId) {
            this.props.dispatch(getPayoutStatusRequest(userId));

            // Also fetch this user's sales so the live work-period row
            // (with the latest TipPayoutStatus) is available in Redux.
            // The location.state snapshot is frozen at navigation time and,
            // depending on the originating page (e.g. Sales report), may
            // not include TipPayoutStatus at all.
            const now = new Date();
            const endDate = now.toISOString().split('T')[0];
            const past = new Date();
            past.setDate(past.getDate() - 90);
            const startDate = past.toISOString().split('T')[0];
            this.props.dispatch(getEmployeeSalesRequest(userId, startDate, endDate));
            this.loadRouteTransferStatus(userId, this.getRouteWorkperiodId());
        }

        if (this.canViewPlatformTipLiquidity()) {
            this.props.dispatch(getTipFundingSummaryRequest());
        }

        // Always load the pooled share — whether this period displays as pooled is
        // period-scoped (backend pooledTipsEnabled), not the account-level setting.
        // Legacy sale-time periods get pooledTipsEnabled=false and keep legacy UI;
        // pooled periods keep pooled history even after the merchant toggle is off.
        this.loadPooledShare();
    }

    loadPooledShare = async () => {
        const userId = this.getCurrentUserId();
        const periodId = this.getRouteWorkperiodId()
            || this.getWorkperiodId(this.getActiveWorkperiod());
        if (!userId || !periodId) return;

        this.setState({ pooledShareLoading: true });
        try {
            const share = await this.props.dispatch(
                getWorkperiodPooledTipShareRequest(periodId, userId)
            );
            if (this._isMounted) this.setState({ pooledShare: share, pooledShareLoading: false });
        } catch (err) {
            if (this._isMounted) {
                this.setState({ pooledShare: null, pooledShareLoading: false });
                toast.warning(err?.message || 'Failed to load pooled tip share');
            }
        }
    }

    handlePayPooledShare = async () => {
        const userId = this.getCurrentUserId();
        const periodId = this.getRouteWorkperiodId()
            || this.getWorkperiodId(this.getActiveWorkperiod());
        const share = this.state.pooledShare;
        if (!userId || !periodId) return;

        const reportShare = Number(share?.shareAmount) || 0;
        const payable = share?.payableAmount !== undefined && share?.payableAmount !== null
            ? Number(share.payableAmount) || 0
            : reportShare;
        if (payable <= 0.01) {
            toast.info('Nothing remains to pay via pooled settlement for this employee.');
            return;
        }
        const managerRouteNote = share?.paidToManager && !share?.stripeTransferId
            ? "\n\nThis employee is not Stripe-eligible — the share will be sent to the designated manager's account, then use Mark as Paid (Manual) after the employee is paid."
            : '';
        const holdNote = '\n\nThe full unpaid share must be available on the platform. If funds are short, the entire share stays held — no partial payment is sent.';
        const confirmMsg = (reportShare > payable + 0.01
            ? `Pay remaining pooled tip share of $${payable.toFixed(2)} via Stripe?\n\n`
              + `Report pool share is $${reportShare.toFixed(2)}; `
              + `$${(reportShare - payable).toFixed(2)} was already settled outside the pool.\n\n`
              + 'This does not pay tips they rang — only their equal clock-session pool share.'
            : `Pay this employee's pooled tip share of $${payable.toFixed(2)} via Stripe?\n\n`
              + 'This does not pay tips they rang — only their equal clock-session pool share.')
            + holdNote
            + managerRouteNote;
        // In-app modal instead of window.confirm — embedded browsers auto-accept
        // native dialogs, which would send money without the user ever seeing this.
        this.setState({ pooledPayConfirmMsg: confirmMsg });
    }

    executePayPooledShare = async () => {
        const userId = this.getCurrentUserId();
        const periodId = this.getRouteWorkperiodId()
            || this.getWorkperiodId(this.getActiveWorkperiod());
        if (!userId || !periodId) return;
        this.setState({ pooledPayConfirmMsg: null, pooledPayLoading: true });
        try {
            const result = await this.props.dispatch(
                payWorkperiodPooledTipsRequest(periodId, userId)
            );
            const parts = [];
            if (result.completed) parts.push(`${result.completed} paid`);
            if (result.skipped) parts.push(`${result.skipped} skipped`);
            if (result.held) parts.push(`${result.held} held until the full share can be paid`);
            if (result.failed) parts.push(`${result.failed} failed`);
            if (result.failed) {
                toast.warning(parts.join('; ') || result.message || 'Pooled tip payout finished with failures');
            } else if (result.held) {
                toast.warning(parts.join('; ') || result.message || 'Share held until the full amount can be paid');
            } else {
                toast.success(parts.join('; ') || result.message || 'Pooled tip share paid');
            }
            await this.loadPooledShare();
            this.loadRouteTransferStatus(userId, periodId);
        } catch (err) {
            // toast in action
        } finally {
            if (this._isMounted) this.setState({ pooledPayLoading: false });
        }
    }

    formatPooledShareStatusLabel = (status, amount) => {
        let label;
        switch (String(status || '').toLowerCase()) {
        case 'completed':
        case 'paid':
            label = 'Pooled share paid';
            break;
        case 'pending_paid_to_manager':
            label = 'Pending payment to employee (paid to manager)';
            break;
        case 'ready':
            label = 'Ready to pay pooled share';
            break;
        case 'accruing':
            label = 'Pooled share accruing';
            break;
        case 'held':
            label = 'Held until the full share can be paid';
            break;
        case 'failed':
            label = 'Pooled share failed';
            break;
        case 'ineligible':
            label = 'Not eligible for pooled payout';
            break;
        case 'settled_outside':
            label = 'Settled outside pooled payout';
            break;
        case 'none':
            label = 'No pooled share';
            break;
        default:
            label = status || '—';
            break;
        }
        const parsed = Number(amount);
        if (!Number.isFinite(parsed)) return label;
        return `${label} — $${parsed.toFixed(2)}`;
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    componentDidUpdate(prevProps) {
        if (this.wasPayoutActionInFlight(prevProps) && !this.isPayoutActionInFlight() && this.canViewPlatformTipLiquidity()) {
            this.props.dispatch(getTipFundingSummaryRequest());
        }

        // After Mark as Paid / skip, reload tip history so Paid/Pending zeros out.
        if (prevProps.skippingTransfersWorkPeriodId && !this.props.skippingTransfersWorkPeriodId) {
            const userId = this.getCurrentUserId();
            const wpId = this.getRouteWorkperiodId()
                || this.getWorkperiodId(this.getActiveWorkperiod());
            if (userId && wpId) {
                this.loadRouteTransferStatus(userId, wpId);
                this.loadPooledShare();
            }
        }

        if (!prevProps.identityRolesLoaded && this.props.identityRolesLoaded && this.canViewPlatformTipLiquidity()) {
            this.props.dispatch(getTipFundingSummaryRequest());
        }

        const userId = this.getCurrentUserId();
        const routeWorkperiodId = this.getRouteWorkperiodId();
        const activeWorkperiod = this.getActiveWorkperiod();
        const activeWorkperiodId = this.getWorkperiodId(activeWorkperiod);
        const payoutAttemptResult = this.getPayoutAttemptResult(activeWorkperiodId, routeWorkperiodId);
        const tipPayoutStatus = this.getTipPayoutStatus(activeWorkperiod);
        const hasNoUnpaidTransfers = typeof payoutAttemptResult?.message === 'string'
            && payoutAttemptResult.message.toLowerCase().includes('no unpaid transfers found');
        const tipsOwed = Number(
            activeWorkperiod?.TipsOwedAmount
            ?? activeWorkperiod?.tipsOwedAmount
            ?? NaN,
        );
        const apiConfirmsPaid = tipPayoutStatus === 'paid'
            && !(Number.isFinite(tipsOwed) && tipsOwed > 0.01);
        // Persist local paid only when sales API confirms paid, or Mark-as-Paid
        // returned "no unpaid transfers". Never trust tip-history alone.
        const confirmedPaid = apiConfirmsPaid || hasNoUnpaidTransfers;

        if (!userId || !confirmedPaid) return;

        if (routeWorkperiodId) {
            rememberWorkPeriodPaidLocally(userId, routeWorkperiodId);
        }

        if (activeWorkperiodId && activeWorkperiodId !== routeWorkperiodId) {
            rememberWorkPeriodPaidLocally(userId, activeWorkperiodId);
        }
    }

    async loadRouteTransferStatus(userId, workPeriodId) {
        if (!userId || !workPeriodId) return;

        const pageSize = 200;
        let page = 1;
        let matchedTransfers = [];

        try {
            while (page <= 5) {
                const res = await axios.get(`/api/stripe/connect/tips/history/${userId}`, {
                    params: { pageSize, page },
                });
                const transfers = Array.isArray(res.data?.transfers) ? res.data.transfers : [];

                matchedTransfers = matchedTransfers.concat(
                    excludeCashTipTransfers(
                        transfers.filter(transfer => Number(transfer.WorkPeriodId || transfer.workPeriodId) === Number(workPeriodId))
                    )
                );

                // Keep paging until this work period is found or history is exhausted.
                // Stopping on the first matched page previously missed older pending
                // rows when newer completed transfers filled earlier pages.
                if (transfers.length < pageSize) {
                    break;
                }

                page += 1;
            }

            const routeTransferStatus = this.deriveRouteTransferStatus(matchedTransfers);
            const routePayoutActions = this.deriveRoutePayoutActions(matchedTransfers);
            if (this._isMounted) {
                this.setState({ routeTransferStatus, routePayoutActions });
            }
        } catch (error) {
            if (this._isMounted) {
                this.setState({ routeTransferStatus: null, routePayoutActions: null });
            }
        }
    }

    /**
     * Surface Reclaim / Retry from tip history so actions appear without requiring
     * a prior (often blocked) "Pay out Tips" click.
     */
    deriveRoutePayoutActions(transfers) {
        const nonCashTransfers = excludeCashTipTransfers(transfers);
        if (!Array.isArray(nonCashTransfers) || nonCashTransfers.length === 0) {
            return null;
        }

        const merchantFunded = [];
        const failedRetry = [];
        let completedAmount = 0;
        let completedCount = 0;
        let employeePaidAmount = 0;
        let skippedSettledAmount = 0;
        let failedAmount = 0;
        let pendingAmount = 0;
        let pendingCount = 0;
        let failedMissingChargeCount = 0;
        let manuallySettled = false;

        nonCashTransfers.forEach((transfer) => {
            const id = Number(transfer.Id ?? transfer.id ?? transfer.TransferId ?? transfer.transferId);
            const status = String(
                transfer.TransferStatus || transfer.transferStatus || transfer.status || '',
            ).toLowerCase();
            const mode = String(
                transfer.TipFundingMode || transfer.tipFundingMode || '',
            ).toLowerCase();
            const stripeId = transfer.StripeTransferId || transfer.stripeTransferId || '';
            const chargeId = transfer.SourceChargeId || transfer.sourceChargeId || '';
            const amount = (Number(transfer.TipAmount ?? transfer.tipAmount) || 0)
                + (Number(transfer.GratuityAmount ?? transfer.gratuityAmount) || 0);
            const isPlatformHeld = mode === 'platformheld';
            const isPlatformReclaimed = mode === 'platformreclaimed';
            const errorMessage = String(transfer.ErrorMessage || transfer.errorMessage || '');
            const alreadyFullyReversed = /already fully reversed|already been reversed/i.test(errorMessage);
            const managerRouted = isManagerRoutedPayReason(errorMessage);
            // Employee Mark as Paid (Manual) stamps a non-manager reason on skipped/completed rows.
            if (
                (status === 'skipped' || status === 'completed')
                && errorMessage.trim()
                && !managerRouted
                && !/^bulk_payout_marker:/i.test(errorMessage.trim())
            ) {
                manuallySettled = true;
            }

            if (status === 'completed' || status === 'skipped') {
                // Manager-routed rows are not employee Paid (residual until Mark as Paid).
                if (!managerRouted) {
                    completedAmount += amount;
                    completedCount += 1;
                    if (status === 'completed' && stripeId) {
                        employeePaidAmount += amount;
                    } else if (status === 'skipped') {
                        skippedSettledAmount += amount;
                        employeePaidAmount += amount;
                    }
                }
            } else if (
                status === 'pending'
                || status === 'awaiting_funds'
                || status === 'awaiting_platform_funds'
                || status === 'awaiting_merchant_funds'
            ) {
                pendingAmount += amount;
                pendingCount += 1;
            } else if (status === 'failed') {
                failedAmount += amount;
                if (Number.isFinite(id)) {
                    failedRetry.push({ id, amount });
                }
                if (!chargeId || /charge/i.test(errorMessage)) {
                    failedMissingChargeCount += 1;
                }
            }

            if (
                Number.isFinite(id)
                && (status === 'pending' || status === 'failed')
                && !stripeId
                && !isPlatformHeld
                && !isPlatformReclaimed
                && !alreadyFullyReversed
            ) {
                merchantFunded.push({ id, amount });
            }
        });

        if (merchantFunded.length === 0 && failedRetry.length === 0 && completedCount === 0) {
            return null;
        }

        const merchantFundedAmount = merchantFunded.reduce((sum, row) => sum + row.amount, 0);

        return {
            merchantFundedTransferIds: merchantFunded.map((row) => row.id),
            merchantFundedAmount,
            merchantFundedCount: merchantFunded.length,
            hasMerchantFundedTransfers: merchantFunded.length > 0,
            canRetryFailedTransfers: failedRetry.length > 0 && merchantFunded.length === 0,
            failedTransfersAvailable: failedRetry.length,
            transfersPending: pendingCount,
            pendingTransfersAvailable: pendingCount,
            completedAmount,
            completedCount,
            employeePaidAmount,
            skippedSettledAmount,
            failedAmount,
            failedCount: failedRetry.length,
            pendingAmount,
            pendingCount,
            failedMissingChargeCount,
            manuallySettled,
            message: merchantFunded.length > 0
                ? `${merchantFunded.length} transfer(s) totalling $${merchantFundedAmount.toFixed(2)} have tips still on the merchant Stripe account. Use Reclaim & Transfer to pay the employee.`
                : null,
        };
    }

    deriveRouteTransferStatus(transfers) {
        const nonCashTransfers = excludeCashTipTransfers(transfers);
        if (!Array.isArray(nonCashTransfers) || nonCashTransfers.length === 0) return null;

        const statuses = nonCashTransfers
            .map(transfer => String(transfer.TransferStatus || transfer.transferStatus || transfer.status || '').toLowerCase())
            .filter(Boolean);

        if (statuses.length === 0) return null;

        const settledStatuses = ['completed', 'skipped'];
        const pendingStatuses = [
            'pending',
            'awaiting_funds',
            'awaiting_platform_funds',
            'awaiting_merchant_funds',
        ];

        if (statuses.every(status => settledStatuses.includes(status))) {
            // Require at least one completed Stripe transfer id before calling this "paid"
            // from history — skipped-only without completed is handled by the sales API.
            const hasCompletedStripePayout = nonCashTransfers.some((transfer) => {
                const status = String(transfer.TransferStatus || transfer.transferStatus || transfer.status || '').toLowerCase();
                const stripeId = transfer.StripeTransferId || transfer.stripeTransferId;
                return status === 'completed' && !!stripeId;
            });
            const allSkipped = statuses.every(status => status === 'skipped');
            if (hasCompletedStripePayout || allSkipped) {
                return 'paid';
            }
            return 'pending';
        }

        if (statuses.some(status => pendingStatuses.includes(status))) {
            return statuses.some(status => settledStatuses.includes(status)) ? 'partial' : 'pending';
        }

        if (statuses.some(status => status === 'failed')) {
            return statuses.some(status => settledStatuses.includes(status)) ? 'partial' : 'failed';
        }

        return null;
    }

    goBack = (e) => {
        if (e) e.preventDefault();
        const returnPath = this.props.location?.state?.returnPath;
        if (returnPath) {
            this.props.history.push(returnPath);
            return;
        }
        this.props.history.goBack();
    }

    wasPayoutActionInFlight = (props) => {
        return !!(
            props.triggeringSaleId
            || props.retryingFailedWorkPeriodId
            || props.skippingTransfersWorkPeriodId
            || props.reclaimingTransfersWorkPeriodId
            || props.reclaimingToPlatformWorkPeriodId
            || props.processingHeldWorkPeriodId
        );
    }

    isPayoutActionInFlight = () => {
        return this.wasPayoutActionInFlight(this.props);
    }

    getFundingNumber = (summary, ...keys) => {
        if (!summary) return 0;
        for (const key of keys) {
            const value = summary[key];
            if (value !== undefined && value !== null && !Number.isNaN(Number(value))) {
                return Number(value);
            }
        }
        return 0;
    }

    canViewPlatformTipLiquidity() {
        return !this.isTipPayoutDisabled()
            && this.props.identityRolesLoaded
            && isRebelityPlatformAdmin(this.props);
    }

    renderPlatformFundingSummary() {
        const { tipFundingSummary, tipFundingSummaryLoading, tipFundingSummaryError } = this.props;

        if (!this.canViewPlatformTipLiquidity()) {
            return null;
        }

        return (
            <div className={s.fundingSummary}>
                <div className={s.fundingSummaryHeader}>
                    <span className={s.fundingSummaryTitle}>{this.props.t('payoutActions.platformLiquidity')}</span>
                    <button
                        type="button"
                        className={s.fundingRefreshBtn}
                        onClick={() => this.props.dispatch(getTipFundingSummaryRequest())}
                        disabled={tipFundingSummaryLoading}
                    >
                        {tipFundingSummaryLoading ? 'Refreshing…' : 'Refresh'}
                    </button>
                </div>

                {tipFundingSummaryError ? (
                    <p className={s.fundingError}>{tipFundingSummaryError}</p>
                ) : null}

                {tipFundingSummaryLoading && !tipFundingSummary ? (
                    <p className={s.fundingNote}>{this.props.t('payoutActions.loadingBalances')}</p>
                ) : null}

                {tipFundingSummary ? (
                    <>
                        <div className={`${s.detailsGrid} ${s.fundingGrid}`}>
                            <div className={s.detailItem}>
                                <span className={s.detailLabel}>{this.props.t('payoutActions.available')}</span>
                                <span className={s.detailValue}>
                                    {this.formatCurrency(this.getFundingNumber(tipFundingSummary, 'PlatformAvailable', 'platformAvailable'))}
                                </span>
                            </div>
                            <div className={s.detailItem}>
                                <span className={s.detailLabel}>{this.props.t('payoutActions.pending')}</span>
                                <span className={s.detailValue}>
                                    {this.formatCurrency(this.getFundingNumber(tipFundingSummary, 'PlatformPending', 'platformPending'))}
                                </span>
                            </div>
                            <div className={s.detailItem}>
                                <span className={s.detailLabel}>{this.props.t('payoutActions.openObligations')}</span>
                                <span className={s.detailValue}>
                                    {this.formatCurrency(this.getFundingNumber(tipFundingSummary, 'OpenObligationAmount', 'openObligationAmount'))}
                                </span>
                            </div>
                            <div className={`${s.detailItem} ${this.getFundingNumber(tipFundingSummary, 'NetExposure', 'netExposure') > 0 ? s.exposureRiskItem : ''}`}>
                                <span className={s.detailLabel}>{this.props.t('payoutActions.netExposure')}</span>
                                <span className={s.detailValue}>
                                    {this.formatCurrency(this.getFundingNumber(tipFundingSummary, 'NetExposure', 'netExposure'))}
                                </span>
                            </div>
                            <div className={`${s.detailItem} ${this.getFundingNumber(tipFundingSummary, 'NetPlatformPosition', 'netPlatformPosition') < 0 ? s.exposureRiskItem : ''}`}>
                                <span className={s.detailLabel}>{this.props.t('payoutActions.netPosition')}</span>
                                <span className={s.detailValue}>
                                    {this.formatCurrency(this.getFundingNumber(tipFundingSummary, 'NetPlatformPosition', 'netPlatformPosition'))}
                                </span>
                            </div>
                            <div className={s.detailItem}>
                                <span className={s.detailLabel}>{this.props.t('payoutActions.held')}</span>
                                <span className={s.detailValue}>
                                    {this.formatCurrency(this.getFundingNumber(tipFundingSummary, 'HeldObligationAmount', 'heldObligationAmount'))}
                                </span>
                            </div>
                        </div>
                        {tipFundingSummary.LiquidityNote || tipFundingSummary.liquidityNote ? (
                            <p className={s.fundingNote}>
                                {tipFundingSummary.LiquidityNote || tipFundingSummary.liquidityNote}
                            </p>
                        ) : (
                            <p className={s.fundingNote}>
                                Pending is unsettled card revenue. Payouts debit available only, so pending does not drop when an employee is paid.
                            </p>
                        )}
                    </>
                ) : null}
            </div>
        );
    }

    formatCurrency = (value) => {
        if (value === null || value === undefined) return '$0.00';
        return `$${parseFloat(value).toFixed(2)}`;
    }

    // ClockIn/ClockOut / WorkperiodDate from employee sales are already merchant-local.
    parseCalendarDate = (raw) => calendarDateFromApi(raw);

    /**
     * Business shift date: prefer ClockIn local calendar day (matches Work Period Detail),
     * then WorkperiodDate as a calendar date — never UTC-midnight of a date-only string.
     */
    getDisplayWorkperiodDate = (workperiod) => {
        if (!workperiod) return null;

        const clockIn = workperiod.ClockIn ?? workperiod.clockIn;
        if (clockIn) {
            const clockInDate = calendarDateFromApi(clockIn);
            if (clockInDate) return clockInDate;
        }

        return calendarDateFromApi(workperiod.WorkperiodDate || workperiod.workperiodDate);
    }

    formatTime = (dateStr) => formatApiTime(dateStr, '-');

    formatDate = (dateStrOrWorkperiod) => {
        if (dateStrOrWorkperiod && typeof dateStrOrWorkperiod === 'object') {
            const clockIn = dateStrOrWorkperiod.ClockIn ?? dateStrOrWorkperiod.clockIn;
            if (clockIn) return formatApiDateLong(clockIn, '');
            const wd = dateStrOrWorkperiod.WorkperiodDate || dateStrOrWorkperiod.workperiodDate;
            return formatApiDateLong(wd, '');
        }
        return formatApiDateLong(dateStrOrWorkperiod, '');
    }

    /** Time only, or date + time when clock-out is more than 24 hours after clock-in. */
    formatClockOut = (clockInStr, clockOutStr) => formatApiClockOut(clockInStr, clockOutStr, '-');


    getEmployeeDisplayName = () => {
        const employee = this.props.selectedEmployee || this.props.location?.state?.employee;
        return employee?.Username || employee?.username || 'Employee';
    }

    handleManualPayout = () => {
        const { match } = this.props;
        const workperiod = this.getActiveWorkperiod();
        const userId = parseInt(match.params.userId);

        if (!workperiod || !userId) return;

        const tips = parseFloat(workperiod.Tips) || 0;
        const gratuity = parseFloat(workperiod.Gratuity) || 0;
        const totalOwed = tips + gratuity;
        const routePayoutActions = this.state.routePayoutActions;
        const remainingAmount = getRemainingTipPayoutAmount({
            totalOwed,
            employeePaidAmount: Number(routePayoutActions?.employeePaidAmount) || 0,
            pendingTransferAmount: Number(routePayoutActions?.pendingAmount) || 0,
        });
        const readiness = this.getManualPayoutReadiness(remainingAmount);
        if (!readiness.ready) {
            toast.error(readiness.reasons[0] || 'Payout is not ready.');
            return;
        }

        const wpId = this.getWorkperiodId(workperiod) || this.getRouteWorkperiodId();
        const saleId = workperiod.Id || workperiod.id;
        this.props.dispatch(
            triggerPayoutRequest(userId, wpId, saleId, this.getEmployeeDisplayName()),
        );
    }

    handleRetryFailedTransfers = () => {
        const { match } = this.props;
        const workperiod = this.getActiveWorkperiod();
        const userId = parseInt(match.params.userId);

        if (workperiod && userId) {
            const wpId = this.getWorkperiodId(workperiod) || this.getRouteWorkperiodId();
            if (!window.confirm('Retry all failed transfers for this work period? This will re-attempt real payments.')) return;
            const saleId = workperiod.Id || workperiod.id;
            this.props.dispatch(
                retryFailedTransfersRequest(userId, wpId, saleId, this.getEmployeeDisplayName()),
            );
        }
    }

    handleSkipMerchantFunded = (transferIds, totalAmount) => {
        const { match } = this.props;
        const workperiod = this.getActiveWorkperiod();
        const userId = parseInt(match.params.userId);

        if (!Array.isArray(transferIds) || transferIds.length === 0 || !workperiod || !userId) return;

        const wpId = this.getWorkperiodId(workperiod) || this.getRouteWorkperiodId();
        const amountText = typeof totalAmount === 'number' ? ` ($${totalAmount.toFixed(2)})` : '';
        const confirmMsg = `Mark ${transferIds.length} pending transfer(s)${amountText} as PAID?\n\n` +
            'Use this only if the employee has already been paid for these tips by other means ' +
            '(e.g., cash, payroll, bank transfer). The Stripe payout will be skipped permanently — ' +
            'no money will move on Stripe.';
        if (!window.confirm(confirmMsg)) return;

        const reason = window.prompt(
            'Optional: enter a short reason (e.g., "Paid in cash on shift"). Leave blank for default.',
            ''
        );
        // window.prompt returns null when user cancels — treat that as cancel.
        if (reason === null) return;

        this.props.dispatch(skipTransfersRequest(
            userId,
            wpId,
            transferIds,
            reason && reason.trim() ? reason.trim() : null,
            workperiod.Id || workperiod.id
        ));
    }

    // Proactive "Mark as Paid (Manual)" — marks every unpaid transfer for this
    // work period as paid externally, without requiring the admin to first
    // click Pay Out and discover individual transfer IDs. This handles the
    // case where tips were captured before Auto-Transfer mode was enabled and
    // the funds physically live in the merchant's Stripe account.
    handleMarkAllAsPaidManual = (totalOwed) => {
        const { match } = this.props;
        const workperiod = this.getActiveWorkperiod();
        const userId = parseInt(match.params.userId, 10);

        if (!workperiod) {
            toast.error('Work period data is still loading. Please try again in a moment.');
            return;
        }
        if (!Number.isFinite(userId) || userId <= 0) {
            toast.error('Missing employee id for this work period.');
            return;
        }

        const wpId = this.getWorkperiodId(workperiod) || this.getRouteWorkperiodId();
        if (!wpId) {
            toast.error('Missing work period id. Refresh the page and try again.');
            return;
        }

        const amountText = typeof totalOwed === 'number' && totalOwed > 0 ? ` ($${totalOwed.toFixed(2)})` : '';
        const confirmMsg = `Mark all unpaid tips for this work period${amountText} as PAID (Manual)?\n\n` +
            'Use this when the employee has already been paid for these tips by other means ' +
            '(e.g., cash, check, payroll, bank transfer) — for example, when tips were captured ' +
            'before Auto-Transfer mode was enabled and the funds went to the merchant.\n\n' +
            'No money will move on Stripe. The work period status will switch to Paid.';
        if (!window.confirm(confirmMsg)) return;

        const reason = window.prompt(
            'A reason is required. Enter why this is being marked as paid manually (e.g., "Paid in cash on 4/24", "Check #1234", "Paid via payroll").',
            ''
        );
        if (reason === null) return;
        if (!reason.trim()) {
            window.alert('A reason is required before marking as paid manually. Please try again and enter a reason.');
            return;
        }

        this.props.dispatch(skipTransfersRequest(
            userId,
            wpId,
            [], // empty → backend skips ALL unpaid transfers for this work period
            reason.trim(),
            workperiod.Id || workperiod.id
        ));
    }

    handleReclaimAndTransfer = (transferIds, totalAmount) => {
        const { match } = this.props;
        const workperiod = this.getActiveWorkperiod();
        const userId = parseInt(match.params.userId);

        if (!Array.isArray(transferIds) || transferIds.length === 0 || !workperiod || !userId) return;

        const wpId = this.getWorkperiodId(workperiod) || this.getRouteWorkperiodId();
        const amountText = typeof totalAmount === 'number' ? ` ($${totalAmount.toFixed(2)})` : '';
        const confirmMsg = `Reclaim${amountText} from the merchant's Stripe account and transfer it to the employee?\n\n` +
            `This will reverse the auto-transfer on the original charge(s) and create a new transfer ` +
            `to the employee's connected account. Real money will move on Stripe.`;
        if (!window.confirm(confirmMsg)) return;

        this.props.dispatch(
            reclaimAndTransferRequest(
                userId,
                wpId,
                transferIds,
                workperiod.Id || workperiod.id,
                this.getEmployeeDisplayName(),
            ),
        );
    }

    handleReclaimToPlatform = (transferIds, totalAmount) => {
        const { match } = this.props;
        const workperiod = this.getActiveWorkperiod();
        const userId = parseInt(match.params.userId);

        if (!workperiod || !userId) return;

        const wpId = this.getWorkperiodId(workperiod) || this.getRouteWorkperiodId();
        const ids = Array.isArray(transferIds) ? transferIds : [];
        const amountText = typeof totalAmount === 'number' ? ` ($${totalAmount.toFixed(2)})` : '';
        const confirmMsg = `Reclaim${amountText} from the merchant's Stripe account back to the platform?\n\n` +
            `This reverses the auto-transfer on the original charge(s). Funds return to the platform balance ` +
            `but are NOT yet sent to the employee. After this completes, click "Pay out Tips" to send the ` +
            `reclaimed funds to the employee.`;
        if (!window.confirm(confirmMsg)) return;

        this.props.dispatch(reclaimToPlatformRequest(userId, wpId, ids, workperiod.Id || workperiod.id));
    }

    handleProcessHeldReversals = () => {
        const { match } = this.props;
        const workperiod = this.getActiveWorkperiod();
        const userId = parseInt(match.params.userId);

        if (!workperiod || !userId) return;

        const wpId = this.getWorkperiodId(workperiod) || this.getRouteWorkperiodId();
        const pooledHeld = !!this.state.pooledShare?.pooledTipsEnabled
            && String(this.state.pooledShare?.status || '').toLowerCase() === 'held';
        const confirmMsg = pooledHeld
            ? 'Retry the held pooled tip share now?\n\n'
              + 'This sends the full unpaid share only when platform funds cover the entire amount. Partial payments are not sent.'
            : 'Try the held tip transfer(s) now?\n\n'
              + "This will re-attempt to reverse the merchant's auto-transfer and send the funds to the employee. "
              + "Use this when the merchant's pending Stripe balance has had time to clear (typically 1-2 business days).";
        if (!window.confirm(confirmMsg)) return;

        this.props.dispatch(processHeldReversalsRequest(userId, wpId, workperiod.Id || workperiod.id));
    }

    getEmployee() {
        return this.props.selectedEmployee || this.props.location.state?.employee;
    }

    getRouteWorkperiodId() {
        const raw = this.props.match?.params?.workperiodId;
        const id = parseInt(raw);
        return Number.isFinite(id) ? id : null;
    }

    getWorkperiodId(workperiod) {
        if (!workperiod) return null;

        const raw = workperiod.WorkPeriodId || workperiod.workPeriodId || workperiod.WorkperiodId || workperiod.workperiodId;
        const id = parseInt(raw);
        return Number.isFinite(id) ? id : null;
    }

    getActiveWorkperiod() {
        return this.props.liveWorkperiod || this.props.location.state?.workperiod || null;
    }

    getTipPayoutStatus(workperiod = this.getActiveWorkperiod()) {
        return workperiod?.TipPayoutStatus || workperiod?.tipPayoutStatus || null;
    }

    getPayoutAttemptResult(activeWorkperiodId = this.getWorkperiodId(this.getActiveWorkperiod()), routeWorkperiodId = this.getRouteWorkperiodId()) {
        const byWorkperiod = this.props.payoutAttemptResultsByWorkPeriodId || {};
        return byWorkperiod[String(activeWorkperiodId)]
            || byWorkperiod[String(routeWorkperiodId)]
            || null;
    }

    handleSendEmail = () => {
        const employee = this.getEmployee();
        const { merchantId } = this.props;
        if (employee && employee.Email) {
            this.props.dispatch(sendOnboardingLinkEmailRequest(
                employee.UserId, merchantId, employee.Email, employee.Username
            ));
        }
    }

    handleSendSms = () => {
        const employee = this.getEmployee();
        const { merchantId } = this.props;
        if (employee && employee.Telephone) {
            this.props.dispatch(sendOnboardingLinkSmsRequest(
                employee.UserId, merchantId, employee.Telephone, employee.Username
            ));
        }
    }

    getShiftDateIso = (workperiod) => {
        const date = this.getDisplayWorkperiodDate(workperiod);
        if (!date) {
            return 'shift';
        }

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    buildWorkPeriodReportContext = (workperiod, options = {}) => {
        const {
            tips,
            gratuity,
            totalOwed,
            cashSales,
            cardSales,
            memberSales,
            fees,
            totalSales,
            hours,
            isActive,
            effectiveTipPayoutStatus,
            manualPayReason,
        } = options;

        const employee = this.props.selectedEmployee || this.props.location?.state?.employee;
        const shiftDateIso = this.getShiftDateIso(workperiod);
        const shiftDateLabel = this.formatDate(workperiod);

        return {
            employeeName: this.getEmployeeDisplayName(),
            storeName: this.props.merchant?.StoreName || '',
            shiftDateIso,
            shiftDateLabel,
            periodLabel: shiftDateLabel,
            startDate: shiftDateIso,
            endDate: shiftDateIso,
            pdfParams: {
                employeeName: this.getEmployeeDisplayName(),
                storeName: this.props.merchant?.StoreName || '',
                shiftDateLabel,
                shiftDateIso,
                t: this.props.t,
                time: {
                    clockIn: this.formatTime(workperiod.ClockIn),
                    clockOut: isActive ? this.props.t('report.stillActive') : this.formatClockOut(workperiod.ClockIn, workperiod.ClockOut),
                    hours: `${hours.toFixed(1)} hrs`,
                },
                sales: {
                    cash: this.formatCurrency(cashSales),
                    card: this.formatCurrency(cardSales),
                    member: this.formatCurrency(memberSales),
                    fees: this.formatCurrency(fees),
                    total: this.formatCurrency(totalSales),
                },
                earnings: {
                    tips: this.formatCurrency(tips),
                    gratuity: this.formatCurrency(gratuity),
                    total: this.formatCurrency(totalOwed),
                },
                payoutStatus: formatTipPayoutStatusLabel(effectiveTipPayoutStatus, manualPayReason, totalOwed),
            },
            email: employee?.Email || employee?.email || '',
            phone: employee?.Telephone || employee?.telephone || '',
        };
    }

    buildSendShiftReportPayload = async (context) => {
        const { buildWorkPeriodPdfBase64 } = await import('../../../utils/workPeriodPdfExport');
        const { fileName, pdfBase64 } = buildWorkPeriodPdfBase64(context.pdfParams);

        return {
            username: context.employeeName,
            storeName: context.storeName,
            startDate: context.startDate,
            endDate: context.endDate,
            periodLabel: context.periodLabel,
            fileName,
            pdfBase64,
        };
    }

    handleSendShiftReportEmail = async (reportContext) => {
        if (!reportContext?.email) {
            toast.info('Add an email address on the employee profile to send reports.');
            return;
        }

        this.setState({ sendReportLoading: 'email' });

        try {
            const payload = await this.buildSendShiftReportPayload(reportContext);
            const userId = parseInt(this.props.match.params.userId, 10);
            await this.props.dispatch(sendEmployeeSalesReportEmailRequest(userId, {
                ...payload,
                email: reportContext.email,
            }));
            toast.success(`Shift report sent to ${reportContext.email}`);
        } catch (error) {
            console.error('Failed to email work period report:', error);
            toast.error(error.message || 'Failed to send shift report.');
        } finally {
            this.setState({ sendReportLoading: null });
        }
    }

    handleSendShiftReportSms = async (reportContext) => {
        if (!reportContext?.phone) {
            toast.info('Add a phone number on the employee profile to send reports.');
            return;
        }

        this.setState({ sendReportLoading: 'sms' });

        try {
            const payload = await this.buildSendShiftReportPayload(reportContext);
            const userId = parseInt(this.props.match.params.userId, 10);
            await this.props.dispatch(sendEmployeeSalesReportSmsRequest(userId, {
                ...payload,
                phone: reportContext.phone,
            }));
            toast.success(`Shift report sent to ${reportContext.phone}`);
        } catch (error) {
            console.error('Failed to text work period report:', error);
            toast.error(error.message || 'Failed to send shift report.');
        } finally {
            this.setState({ sendReportLoading: null });
        }
    }

    // Returns the current URL userId (the user this page is rendering for).
    getCurrentUserId() {
        const raw = this.props.match?.params?.userId;
        const id = parseInt(raw);
        return Number.isFinite(id) ? id : null;
    }

    // Payout status from Redux is a single global slot — only trust it when
    // the response is for the user we are currently viewing. Otherwise it may
    // be stale from a previously-viewed employee, which would briefly flash
    // the "Send Setup Link" panel for already-connected users.
    getPayoutStatusForCurrentUser() {
        const userId = this.getCurrentUserId();
        const ps = this.props.payoutStatus;
        if (!userId || !ps || ps._userId !== userId) return null;
        return ps;
    }

    // True while we are still resolving payout status for THIS user. Used to
    // suppress the setup-link panel until we know whether the employee is
    // already connected. (Stale data from a different user is also treated
    // as "still loading" for this user.)
    isPayoutStatusUnknownForCurrentUser() {
        const userId = this.getCurrentUserId();
        if (!userId) return false;
        if (this.props.payoutStatusLoading && this.props.payoutStatusLoadingUserId === userId) return true;
        // Have data, but not for this user — treat as unknown.
        if (this.props.payoutStatus && this.props.payoutStatus._userId !== userId) return true;
        // No data at all yet for this user.
        if (!this.props.payoutStatus) return true;
        return false;
    }

    hasStripeSetup() {
        const ps = this.getPayoutStatusForCurrentUser();
        const employee = this.getEmployee();
        return !!((ps && ps.stripeAccountId) ||
            (employee?.StripeAccountId && employee.StripeAccountId !== ''));
    }

    isStripeEligible() {
        const ps = this.getPayoutStatusForCurrentUser();
        const status = ps?.accountStatus || ps?.stripeAccountStatus || ps?.StripeAccountStatus;
        return this.hasStripeSetup() && String(status || '').toLowerCase() === 'active';
    }

    isEmployeeTipPayoutEnabled() {
        const ps = this.getPayoutStatusForCurrentUser();
        if (!ps) return false;
        if (ps.tipPayoutEnabled === true || ps.TipPayoutEnabled === true) return true;
        if (ps.tipPayoutEnabled === false || ps.TipPayoutEnabled === false) return false;
        return false;
    }

    isTipPayoutDisabled() {
        return Number(this.props.tipPayoutRoutingMode) === 3;
    }

    isMerchantTipRoutingMode() {
        return Number(this.props.tipPayoutRoutingMode) === 2;
    }

    /**
     * Gate for the green "Pay out Tips" CTA using what this view can verify.
     *
     * Merchant Account routing reclaims from the merchant per sale, then pays the
     * employee from platform Available — so merchant routing is allowed, and
     * merchant-wide Net Position is not used as a veto (Available vs this amount is).
     */
    getManualPayoutReadiness(totalOwed) {
        const reasons = [];
        const owed = Number(totalOwed) || 0;

        if (this.isPayoutStatusUnknownForCurrentUser()) {
            reasons.push('Waiting for employee Stripe status…');
        } else if (!this.isStripeEligible()) {
            reasons.push('Employee Stripe Connect account must be active.');
        } else if (!this.isEmployeeTipPayoutEnabled()) {
            reasons.push('Tip payouts are not enabled for this employee.');
        }

        if (this.isTipPayoutDisabled()) {
            reasons.push('Tip payouts are disabled for this store.');
        }

        if (this.canViewPlatformTipLiquidity()) {
            const { tipFundingSummary, tipFundingSummaryLoading, tipFundingSummaryError } = this.props;
            if (tipFundingSummaryLoading && !tipFundingSummary) {
                reasons.push('Waiting for platform tip liquidity…');
            } else if (tipFundingSummaryError && !tipFundingSummary) {
                reasons.push('Platform tip liquidity could not be loaded.');
            } else if (tipFundingSummary) {
                const available = this.getFundingNumber(tipFundingSummary, 'PlatformAvailable', 'platformAvailable');
                if (available + 0.005 < owed) {
                    reasons.push(
                        `Platform Available (${this.formatCurrency(available)}) is less than this payout (${this.formatCurrency(owed)}).`
                    );
                }
            }
            // If summary is simply not loaded yet for an admin, do not hard-block —
            // merchant-routed reclaim does not require platform Available up front.
        }
        // Non-admins cannot load platform liquidity; do not block Pay out / Reclaim.
        // Merchant tip routing reclaims from the merchant account first.

        return {
            ready: reasons.length === 0,
            reasons,
        };
    }

    render() {
        const { location } = this.props;
        const workperiod = this.getActiveWorkperiod();
        // Merge employee from location state and Redux (fallback)
        // Prefer Redux selectedEmployee (has full user data from API),
        // fall back to location state employee (may be minimal)
        const employee = this.props.selectedEmployee || location.state?.employee;

        const routeWorkperiodId = this.getRouteWorkperiodId();
        const wpId = this.getWorkperiodId(workperiod) || routeWorkperiodId;
        // Prefer the live Redux row's TipPayoutStatus over the frozen
        // location.state snapshot. Actions like Mark as Paid (Manual) update
        // employeeSales[*].TipPayoutStatus in Redux but not the navigation
        // snapshot, so without this the badge / action buttons stay stuck on
        // "Pending" until a hard refresh.
        const liveWp = this.props.liveWorkperiod;
        const tipPayoutStatus = this.getTipPayoutStatus(workperiod);
        const transferId = liveWp?.TransferId || liveWp?.transferId || workperiod?.TransferId || workperiod?.transferId || null;

        if (!workperiod) {
            return (
                <div className="page-wrapper">
                    <div className="page-content">
                        <div className={s.loaderContainer}>
                            <Loader size={60} />
                        </div>
                    </div>
                </div>
            );
        }

        const tips = parseFloat(workperiod.Tips) || 0;
        const gratuity = parseFloat(workperiod.Gratuity) || 0;
        const totalOwed = tips + gratuity;
        const saleTotals = salesAmounts(workperiod);
        const refundNets = reportingNets(workperiod);
        const cashSales = saleTotals.cash;
        const cardSales = saleTotals.card;
        const memberSales = saleTotals.member;
        const fees = saleTotals.fees;
        const totalSales = saleTotals.total;
        const isActive = !workperiod.ClockOut || String(workperiod.ClockOut).trim() === '' || String(workperiod.ClockOut).trim() === '0001-01-01T00:00:00';
        const currentSaleId = workperiod.Id || workperiod.id || null;
        const payoutAttemptResult = this.getPayoutAttemptResult(wpId, routeWorkperiodId);
        const routePayoutActions = this.state.routePayoutActions;
        const hasNoUnpaidTransfers = typeof payoutAttemptResult?.message === 'string'
            && payoutAttemptResult.message.toLowerCase().includes('no unpaid transfers found');
        const isLocallyPaid = isWorkPeriodMarkedPaidLocally(this.getCurrentUserId(), routeWorkperiodId)
            || isWorkPeriodMarkedPaidLocally(this.getCurrentUserId(), wpId);
        const tipsOwedAmount = workperiod.TipsOwedAmount ?? workperiod.tipsOwedAmount;
        const manualPayReason = resolveManualPayReasonFromRow(
            workperiod,
            payoutAttemptResult?.manualPayReason !== undefined && payoutAttemptResult?.manualPayReason !== null
                ? payoutAttemptResult.manualPayReason
                : (getLocalManualPayReason(this.getCurrentUserId(), wpId)
                    || getLocalManualPayReason(this.getCurrentUserId(), routeWorkperiodId)
                    || undefined)
        );
        const employeeSettledAmount = Number(routePayoutActions?.employeePaidAmount) || 0;
        const effectiveTipPayoutStatus = resolveEffectiveTipPayoutStatus({
            apiStatus: tipPayoutStatus,
            routeTransferStatus: this.state.routeTransferStatus,
            isLocallyPaid,
            hasNoUnpaidTransfers,
            tipsOwedAmount,
            manualPayReason,
            employeeSettledAmount,
            tipsEarnedAmount: totalOwed,
        });
        const showRetryFailedButton = !!(
            (payoutAttemptResult?.canRetryFailedTransfers && payoutAttemptResult?.failedTransfersAvailable > 0)
            || (routePayoutActions?.canRetryFailedTransfers && routePayoutActions?.failedTransfersAvailable > 0)
        );
        const showPendingNotice = !!(
            payoutAttemptResult?.transfersPending > 0
            || payoutAttemptResult?.pendingTransfersAvailable > 0
            || routePayoutActions?.transfersPending > 0
            || routePayoutActions?.pendingTransfersAvailable > 0
        );
        // Prefer tip-history merchant-funded classification when available — it excludes
        // PlatformReclaimed / already-fully-reversed rows that stale payoutAttemptResult
        // may still list after a failed Reclaim toast.
        const merchantFundedTransferIds = (
            Array.isArray(routePayoutActions?.merchantFundedTransferIds)
        )
            ? routePayoutActions.merchantFundedTransferIds
            : (
                Array.isArray(payoutAttemptResult?.merchantFundedTransferIds)
                    ? payoutAttemptResult.merchantFundedTransferIds
                    : []
            );
        const merchantFundedAmount = Number(
            (Array.isArray(routePayoutActions?.merchantFundedTransferIds)
                ? routePayoutActions?.merchantFundedAmount
                : (payoutAttemptResult?.merchantFundedAmount > 0
                    ? payoutAttemptResult.merchantFundedAmount
                    : routePayoutActions?.merchantFundedAmount))
            || 0,
        );
        const showMerchantFundedActions = merchantFundedTransferIds.length > 0;
        const heldTransferIds = Array.isArray(payoutAttemptResult?.heldTransferIds) ? payoutAttemptResult.heldTransferIds : [];
        const heldAmount = Number(payoutAttemptResult?.heldAmount) || 0;
        const heldCount = Number(payoutAttemptResult?.heldCount) || heldTransferIds.length;
        const showHeldSection = !!payoutAttemptResult?.hasHeldTransfers || heldCount > 0;
        const payoutActionMessage = payoutAttemptResult?.message || routePayoutActions?.message || null;
        const employeePaidAmount = Number(routePayoutActions?.employeePaidAmount) || 0;
        const transferSummary = {
            completedAmount: employeePaidAmount > 0
                ? employeePaidAmount
                : (Number(routePayoutActions?.completedAmount) || 0),
            completedCount: Number(routePayoutActions?.completedCount) || 0,
            failedAmount: Number(routePayoutActions?.failedAmount) || 0,
            failedCount: Number(routePayoutActions?.failedCount) || 0,
            pendingAmount: Number(routePayoutActions?.pendingAmount) || 0,
            pendingCount: Number(routePayoutActions?.pendingCount) || 0,
            failedMissingChargeCount: Number(routePayoutActions?.failedMissingChargeCount) || 0,
        };
        const isRealManualPayReason = !!(
            manualPayReason
            && !isManagerRoutedPayReason(manualPayReason)
            && !isPooledDeferredPayReason(manualPayReason)
        );
        const isManuallySettled = !!(
            routePayoutActions?.manuallySettled
            || isRealManualPayReason
            || effectiveTipPayoutStatus === 'paid'
            || (isLocallyPaid && isRealManualPayReason)
        );
        const payoutAmountSplit = buildWorkPeriodPayoutAmountSplit({
            totalOwed,
            employeePaidAmount,
            manuallySettled: isManuallySettled,
        });
        const remainingPayoutAmount = isManuallySettled
            ? 0
            : getRemainingTipPayoutAmount({
                totalOwed,
                employeePaidAmount,
                pendingTransferAmount: transferSummary.pendingAmount,
            });
        // When tip history shows the shift obligation is covered by employee payouts,
        // prefer paid over leftover failed/partial/manager-recast API status.
        // True manager-routed rows are excluded from completedAmount, so they will
        // not cover the obligation and stay pending_paid_to_manager.
        // Never hide open unpaid TipTransfer rows (they still appear in Available Tips).
        const historyCoversObligation = transferSummary.completedAmount + 0.005 >= totalOwed
            && totalOwed > 0
            && transferSummary.pendingCount <= 0;
        let displayTipPayoutStatus = (
            historyCoversObligation
            && effectiveTipPayoutStatus !== 'paid'
            && effectiveTipPayoutStatus !== 'pending'
        )
            ? 'paid'
            : effectiveTipPayoutStatus;
        const displayManualPayReason = (
            displayTipPayoutStatus === 'paid'
            && isManagerRoutedPayReason(manualPayReason)
            && (historyCoversObligation || employeePaidAmount + 0.01 >= totalOwed)
        ) ? null : manualPayReason;
        // Mixed employee payout: tip history paid some of Total Earnings; remainder still held.
        if (
            payoutAmountSplit.showSplit
            && (displayTipPayoutStatus === 'pending' || displayTipPayoutStatus === 'failed')
        ) {
            displayTipPayoutStatus = 'partial';
        }
        const apiTipPayoutNote = resolveTipPayoutNoteFromRow(workperiod);
        const splitNotes = buildSplitPayoutStatusNotes({
            status: displayTipPayoutStatus,
            totalOwed,
            ...transferSummary,
            completedAmount: transferSummary.completedAmount,
            // Pass raw open-row pending so notes can explain leftover vs remaining owed.
            pendingAmount: transferSummary.pendingAmount,
            pendingCount: transferSummary.pendingCount,
        });
        const tipPayoutStatusNotes = (
            apiTipPayoutNote
            && !(displayTipPayoutStatus === 'paid' && isManagerRoutedPayReason(manualPayReason))
        )
            ? [apiTipPayoutNote]
            : splitNotes;
        const hasLeftoverFailedAfterPaid = displayTipPayoutStatus === 'paid'
            && transferSummary.failedCount > 0;
        // Hide Retry when paid, or when every failure lacks a Stripe charge (retry cannot succeed).
        const allFailuresMissingCharge = transferSummary.failedCount > 0
            && transferSummary.failedMissingChargeCount >= transferSummary.failedCount;
        const showRetryFailedForDisplay = showRetryFailedButton
            && displayTipPayoutStatus !== 'paid'
            && !allFailuresMissingCharge;
        const isTriggeringPayout = currentSaleId != null && this.props.triggeringSaleId === currentSaleId;
        const isRetryingFailedTransfers = String(this.props.retryingFailedWorkPeriodId) === String(wpId);
        const isSkippingTransfers = String(this.props.skippingTransfersWorkPeriodId) === String(wpId);
        const isReclaimingTransfers = String(this.props.reclaimingTransfersWorkPeriodId) === String(wpId);
        const isReclaimingToPlatform = String(this.props.reclaimingToPlatformWorkPeriodId) === String(wpId);
        const isProcessingHeld = String(this.props.processingHeldWorkPeriodId) === String(wpId);
        const anyActionInFlight = isTriggeringPayout || isRetryingFailedTransfers || isSkippingTransfers || isReclaimingTransfers || isReclaimingToPlatform || isProcessingHeld || this.state.pooledPayLoading;
        const payoutReadiness = this.getManualPayoutReadiness(remainingPayoutAmount);
        const canGuaranteedPayout = payoutReadiness.ready;
        // Period-scoped: pooled UI renders only when THIS work period ran pooled
        // (backend pooledTipsEnabled), never from the account-level toggle alone.
        const pooledShare = this.state.pooledShare;
        const pooledTipsOn = !!pooledShare?.pooledTipsEnabled;
        const pooledShareAmount = Number(pooledShare?.shareAmount) || 0;
        const pooledPayableAmount = pooledShare?.payableAmount !== undefined && pooledShare?.payableAmount !== null
            ? (Number(pooledShare.payableAmount) || 0)
            : pooledShareAmount;
        const pooledAlreadySettled = Number(pooledShare?.alreadySettledOutsidePooledAmount) || 0;
        // paidToManager=true before payment only means the settlement will ROUTE to the
        // designated manager (employee not Stripe-eligible) — still payable. Only hide
        // the pay button once a manager-routed payment actually exists.
        const pooledManagerAlreadyPaid = pooledShare?.status === 'pending_paid_to_manager'
            || (pooledShare?.paidToManager && pooledShare?.stripeTransferId);
        const showPooledPayButton = !!(
            pooledShare?.showPayButton
            && !pooledManagerAlreadyPaid
            && !pooledShare?.employeePaid
            && pooledShare?.periodEnded
            && pooledPayableAmount > 0.01
        );
        const showPooledManualPay = !!(
            (pooledShare?.showManualPay || pooledShare?.paidToManager)
            && !pooledShare?.employeePaid
            && pooledShare?.status !== 'paid'
            && pooledShare?.status !== 'completed'
        );
        const pooledPaidToManager = !!pooledShare?.paidToManager;
        let hours = parseFloat(workperiod.Hours) || 0;
        if (isActive && workperiod.ClockIn) {
            const clockInDate = parseApiDateTime(workperiod.ClockIn);
            if (clockInDate) {
                const diffMs = new Date() - clockInDate;
                hours = diffMs > 0 ? diffMs / 3600000 : 0;
            }
        }

        const reportContext = this.buildWorkPeriodReportContext(workperiod, {
            tips,
            gratuity,
            totalOwed,
            cashSales,
            cardSales,
            memberSales,
            fees,
            totalSales,
            hours,
            isActive,
            effectiveTipPayoutStatus: displayTipPayoutStatus,
            manualPayReason: displayManualPayReason,
        });
        const { sendReportLoading } = this.state;
        const { t } = this.props;
        const reportActionBusy = !!sendReportLoading;

        return (
            <div className="page-wrapper">
                <div className="page-content">
                    <div className={s.header}>
                        <div className={s.headerLeft}>
                            <a className="back-link" href="#" onClick={this.goBack}>
                                <i className="zmdi zmdi-arrow-left"></i>
                            </a>
                            <div>
                                <h1 className={s.title}>
                                    {employee?.Username || t('report.employee')}
                                </h1>
                                <p className={s.subtitle}>
                                    {employee?.Username || t('report.employee')} - {this.formatDate(workperiod)}
                                </p>
                            </div>
                        </div>
                        <div className={s.headerActions}>
                            <button
                                type="button"
                                className={s.sendReportBtn}
                                onClick={() => this.handleSendShiftReportEmail(reportContext)}
                                disabled={reportActionBusy || !reportContext.email}
                                title={reportContext.email ? t('report.emailPdf', { email: reportContext.email }) : t('report.noEmail')}
                                aria-label={t('report.emailPdf', { email: reportContext.email || '' })}
                            >
                                {sendReportLoading === 'email' ? (
                                    <Loader size={16} />
                                ) : (
                                    <i className="zmdi zmdi-email" aria-hidden="true" />
                                )}
                            </button>
                            <button
                                type="button"
                                className={s.sendReportBtn}
                                onClick={() => this.handleSendShiftReportSms(reportContext)}
                                disabled={reportActionBusy || !reportContext.phone}
                                title={reportContext.phone ? t('report.textPdf', { phone: reportContext.phone }) : t('report.noPhone')}
                                aria-label={t('report.textPdf', { phone: reportContext.phone || '' })}
                            >
                                {sendReportLoading === 'sms' ? (
                                    <Loader size={16} />
                                ) : (
                                    <i className="zmdi zmdi-smartphone" aria-hidden="true" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Time Card */}
                    <Widget>
                        <div className={s.section}>
                            <h3 className={s.sectionTitle}>
                                <i className="zmdi zmdi-time"></i> {t('report.time')}
                            </h3>
                            <div className={s.detailsGrid}>
                                <div className={s.detailItem}>
                                    <span className={s.detailLabel}>{t('report.clockIn')}</span>
                                    <span className={s.detailValue}>{this.formatTime(workperiod.ClockIn)}</span>
                                </div>
                                <div className={s.detailItem}>
                                    <span className={s.detailLabel}>{t('report.clockOut')}</span>
                                    <span className={s.detailValue}>
                                        {isActive
                                            ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#2ecc71' }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#2ecc71', display: 'inline-block', animation: 'pulse-dot 1.5s ease-in-out infinite' }}></span> {t('report.stillActive')}</span>
                                            : this.formatClockOut(workperiod.ClockIn, workperiod.ClockOut)}
                                    </span>
                                </div>
                                <div className={s.detailItem}>
                                    <span className={s.detailLabel}>{t('report.totalHours')}</span>
                                    <span className={s.detailValue} style={isActive ? { color: '#2ecc71' } : undefined}>{hours.toFixed(1)} {t('report.hrs')}</span>
                                </div>
                            </div>
                        </div>
                    </Widget>

                    {/* Sales Card */}
                    <Widget>
                        <div className={s.section}>
                            <h3 className={s.sectionTitle}>
                                <i className="zmdi zmdi-shopping-cart"></i> {t('report.sales')}
                            </h3>
                            <div className={s.detailsGrid}>
                                <div className={s.detailItem}>
                                    <span className={s.detailLabel}>{t('report.cashSales')}</span>
                                    <span className={s.detailValue}>{this.formatCurrency(cashSales)}</span>
                                </div>
                                <div className={s.detailItem}>
                                    <span className={s.detailLabel}>{t('report.cardSales')}</span>
                                    <span className={s.detailValue}>{this.formatCurrency(cardSales)}</span>
                                </div>
                                <div className={s.detailItem}>
                                    <span className={s.detailLabel}>{t('report.memberSales')}</span>
                                    <span className={s.detailValue}>{this.formatCurrency(memberSales)}</span>
                                </div>
                                {/* Fees stay on downloadable / emailed PDF only — not shown on-screen. */}
                                <div className={`${s.detailItem} ${s.totalItem}`}>
                                    <span className={s.detailLabel}>{t('report.salesBeforeRefunds')}</span>
                                    <span className={s.detailValue}>{this.formatCurrency(totalSales)}</span>
                                </div>
                            </div>
                            {hasRefundReporting(refundNets) ? (
                                <>
                                    <h3 className={s.sectionTitle} style={{ marginTop: 16 }}>
                                        <i className="zmdi zmdi-replay"></i> {t('report.afterRefunds')}
                                    </h3>
                                    <div className={s.detailsGrid}>
                                        {refundNets.discounts > 0 ? (
                                            <div className={s.detailItem}>
                                                <span className={s.detailLabel}>{t('report.discounts')}</span>
                                                <span className={s.detailValue}>{this.formatCurrency(refundNets.discounts)}</span>
                                            </div>
                                        ) : null}
                                        {refundNets.comps > 0 ? (
                                            <div className={s.detailItem}>
                                                <span className={s.detailLabel}>{t('report.comps')}</span>
                                                <span className={s.detailValue}>{this.formatCurrency(refundNets.comps)}</span>
                                            </div>
                                        ) : null}
                                        {refundNets.merchRefunds > 0 ? (
                                            <div className={s.detailItem} title={t('report.merchRefundsHelp')}>
                                                <span className={s.detailLabel}>{t('report.merchRefunds')}</span>
                                                <span className={s.detailValue}>{this.formatCurrency(refundNets.merchRefunds)}</span>
                                            </div>
                                        ) : null}
                                        {refundNets.customerRefunds > 0 ? (
                                            <div className={s.detailItem} title={t('report.customerRefundsHelp')}>
                                                <span className={s.detailLabel}>{t('report.customerRefunds')}</span>
                                                <span className={s.detailValue}>{this.formatCurrency(refundNets.customerRefunds)}</span>
                                            </div>
                                        ) : null}
                                        {refundNets.tipRefundedDisplay > 0 ? (
                                            <div className={s.detailItem}>
                                                <span className={s.detailLabel}>{t('report.netTips')}</span>
                                                <span className={s.detailValue}>{this.formatCurrency(refundNets.netTipsDisplay)}</span>
                                            </div>
                                        ) : null}
                                        {refundNets.gratuityRefundedDisplay > 0 ? (
                                            <div className={s.detailItem}>
                                                <span className={s.detailLabel}>{t('report.netGratuity')}</span>
                                                <span className={s.detailValue}>{this.formatCurrency(refundNets.netGratuityDisplay)}</span>
                                            </div>
                                        ) : null}
                                        {refundNets.merchRefunds > 0 ? (
                                            <div className={`${s.detailItem} ${s.totalItem}`}>
                                                <span className={s.detailLabel}>{t('report.salesAfterRefunds')}</span>
                                                <span className={s.detailValue}>{this.formatCurrency(refundNets.netSalesMerch)}</span>
                                            </div>
                                        ) : null}
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: 'var(--text-light2)', margin: '12px 0 0' }}>
                                        {refundNets.merchRefunds > 0 ? (
                                            <p style={{ margin: '0 0 6px' }}>{t('report.merchRefundsHelp')}</p>
                                        ) : null}
                                        {refundNets.customerRefunds > 0 ? (
                                            <p style={{ margin: '0 0 6px' }}>{t('report.customerRefundsHelp')}</p>
                                        ) : null}
                                        <p style={{ margin: 0 }}>
                                            {t(refundNets.merchRefundEstimated || refundNets.tipGratRefundEstimated
                                                ? 'report.refundEstimateFootnote'
                                                : 'report.refundNetFootnote')}
                                        </p>
                                    </div>
                                </>
                            ) : null}
                        </div>
                    </Widget>

                    {/* Tips & Earnings Card */}
                    <Widget>
                        <div className={s.section}>
                            <h3 className={s.sectionTitle}>
                                <i className="zmdi zmdi-money"></i> {t('report.tipsEarnings')}
                            </h3>
                            <div className={s.detailsGrid}>
                                <div className={s.detailItem}>
                                    <span className={s.detailLabel}>{t('report.tips')}</span>
                                    <span className={s.detailValue}>{this.formatCurrency(tips)}</span>
                                </div>
                                <div className={s.detailItem}>
                                    <span className={s.detailLabel}>{t('report.gratuity')}</span>
                                    <span className={s.detailValue}>{this.formatCurrency(gratuity)}</span>
                                </div>
                                <div className={`${s.detailItem}${totalOwed > 0.01 ? ` ${s.owedItem}` : ''}`}>
                                    <span className={s.detailLabel}>{t('report.totalEarnings')}</span>
                                    <span className={s.detailValue}>{this.formatCurrency(totalOwed)}</span>
                                </div>
                            </div>
                        </div>
                    </Widget>

                    {/* Payout Status Card */}
                    <Widget>
                        <div className={s.section}>
                            <h3 className={s.sectionTitle}>
                                <i className="zmdi zmdi-card"></i> {t('report.payoutStatus')}
                            </h3>
                            <div className={s.detailsGrid}>
                                <div className={`${s.detailItem} ${s.statusItem}`}>
                                    <span className={s.detailLabel}>{t('report.status')}</span>
                                    {this.props.employeeSalesLoading && !tipPayoutStatus ? (
                                        <span className={s.detailValue}>{t('common.loading')}</span>
                                    ) : (() => {
                                        const managerStatusAmount = (
                                            displayTipPayoutStatus === 'pending_paid_to_manager'
                                            || isManagerRoutedPayReason(displayManualPayReason)
                                        )
                                            ? (totalOwed > 0 ? totalOwed : remainingPayoutAmount)
                                            : undefined;
                                        const statusLabel = formatTipPayoutStatusLabel(
                                            displayTipPayoutStatus,
                                            displayManualPayReason,
                                            managerStatusAmount,
                                        );
                                        return (
                                            <span className={`${s.statusBadge} ${s[displayTipPayoutStatus || 'none']}`}>
                                                {statusLabel}
                                            </span>
                                        );
                                    })()}
                                </div>
                                {payoutAmountSplit.showSplit ? (
                                    <>
                                        <div className={s.detailItem}>
                                            <span className={s.detailLabel}>{t('report.paid')}</span>
                                            <span className={`${s.detailValue} ${s.paidAmountValue}`}>
                                                {this.formatCurrency(payoutAmountSplit.paidAmount)}
                                            </span>
                                        </div>
                                        <div className={s.detailItem}>
                                            <span className={s.detailLabel}>{t('report.pending')}</span>
                                            <span className={`${s.detailValue} ${s.pendingAmountValue}`}>
                                                {this.formatCurrency(payoutAmountSplit.pendingAmount)}
                                            </span>
                                        </div>
                                    </>
                                ) : null}
                                {tipPayoutStatusNotes.length > 0 ? (
                                    <div className={s.detailItem} style={{ gridColumn: '1 / -1' }}>
                                        {tipPayoutStatusNotes.map((note) => (
                                            <p key={note} className={`${s.payoutNote} ${s.warningNote}`} style={{ marginTop: 8, marginBottom: 0 }}>
                                                {note}
                                            </p>
                                        ))}
                                    </div>
                                ) : null}
                                {transferId ? (
                                    <div className={s.detailItem}>
                                        <span className={s.detailLabel}>{t('report.transferId')}</span>
                                        <span className={s.detailValue} style={{ fontSize: '0.8rem' }}>
                                            {transferId}
                                        </span>
                                    </div>
                                ) : null}
                            </div>

                            {this.renderPlatformFundingSummary()}

                            {pooledTipsOn ? (
                                <div className={s.payoutAction}>
                                    <p className={`${s.payoutNote} ${s.infoNote}`}>
                                        Pooled Tips is on. Sale-time tip payout is unavailable.
                                        {' '}This employee&apos;s pool share
                                        {this.state.pooledShareLoading
                                            ? ' is loading…'
                                            : ` is ${this.formatCurrency(pooledShareAmount)}`}
                                        {this.state.pooledShareLoading
                                            ? ''
                                            : (pooledShare?.periodEnded
                                                ? ' (matches Pooled Tip Report).'
                                                : ' (accruing until the work period ends; matches Pooled Tip Report).')}
                                    </p>
                                    {!this.state.pooledShareLoading && pooledAlreadySettled > 0.01 ? (
                                        <p className={`${s.payoutNote} ${s.infoNote}`}>
                                            {this.formatCurrency(pooledAlreadySettled)} already settled outside the pool
                                            {pooledPayableAmount > 0.01
                                                ? `; ${this.formatCurrency(pooledPayableAmount)} remains for pooled settlement.`
                                                : '; nothing remains for pooled settlement.'}
                                        </p>
                                    ) : null}
                                    {pooledShare?.message ? (
                                        <p className={`${s.payoutNote} ${pooledPaidToManager ? s.warningNote : (pooledShare.employeePaid ? s.infoNote : s.warningNote)}`}>
                                            {pooledShare.message}
                                        </p>
                                    ) : null}
                                    <div className={s.detailsGrid} style={{ marginBottom: 12 }}>
                                        <div className={`${s.detailItem} ${s.statusItem}`}>
                                            <span className={s.detailLabel}>{t('payoutActions.pooledShareStatus')}</span>
                                            <span className={`${s.statusBadge} ${s[pooledShare?.employeePaid ? 'paid' : (pooledPaidToManager ? 'pending' : (pooledShare?.status === 'ready' || pooledShare?.status === 'held' ? 'pending' : 'none'))]}`}>
                                                {this.state.pooledShareLoading
                                                    ? 'Loading…'
                                                    : this.formatPooledShareStatusLabel(
                                                        pooledShare?.status
                                                            || (pooledShare && pooledShare.periodEnded === false
                                                                ? 'accruing'
                                                                : (pooledShare ? 'none' : null)),
                                                        pooledShareAmount,
                                                    )}
                                            </span>
                                        </div>
                                        <div className={s.detailItem}>
                                            <span className={s.detailLabel}>{t('payoutActions.poolShare')}</span>
                                            <span className={s.detailValue}>
                                                {this.formatCurrency(pooledShareAmount)}
                                            </span>
                                        </div>
                                        {pooledAlreadySettled > 0.01 || (pooledShare?.periodEnded && pooledPayableAmount !== pooledShareAmount) ? (
                                            <div className={s.detailItem}>
                                                <span className={s.detailLabel}>{t('payoutActions.payableNow')}</span>
                                                <span className={s.detailValue}>
                                                    {this.formatCurrency(pooledPayableAmount)}
                                                </span>
                                            </div>
                                        ) : null}
                                    </div>
                                    {showPooledPayButton ? (
                                        <>
                                            <button
                                                type="button"
                                                className={s.payoutBtn}
                                                onClick={this.handlePayPooledShare}
                                                disabled={anyActionInFlight || this.state.pooledShareLoading || !pooledShare?.canPay}
                                                title="Pay this employee's unpaid pooled tip share via Stripe"
                                            >
                                                {this.state.pooledPayLoading ? (
                                                    <><Loader size={16} /> {t('payoutActions.payingPooled')}</>
                                                ) : (
                                                    <><i className="zmdi zmdi-money"></i> Pay out pooled tip ({this.formatCurrency(pooledPayableAmount)})</>
                                                )}
                                            </button>
                                            <p className={s.payoutMeta}>
                                                {pooledShare?.status === 'held'
                                                    ? 'Pays the full unpaid share only when platform funds cover the entire amount. Partial payments are not sent.'
                                                    : 'Pays only this employee\u2019s unpaid clock-session pool share. The full unpaid amount must be available \u2014 no partial Stripe pay.'}
                                            </p>
                                        </>
                                    ) : null}
                                    {showPooledManualPay ? (
                                    <button
                                        type="button"
                                        className={s.skipBtn}
                                        onClick={() => this.handleMarkAllAsPaidManual(pooledPayableAmount || pooledShareAmount || remainingPayoutAmount)}
                                        disabled={anyActionInFlight}
                                        style={{ width: '100%', marginTop: 10 }}
                                        title="Use after the employee has been paid (including when the share went to the designated manager)"
                                    >
                                        {isSkippingTransfers ? (
                                            <><Loader size={16} /> {t('payoutActions.markingPaid')}</>
                                            ) : (
                                            <><i className="zmdi zmdi-check-all"></i> {t('payoutActions.markPaid')}</>
                                        )}
                                    </button>
                                    ) : null}
                                    {this.state.pooledPayConfirmMsg ? (
                                        <div className={s.confirmOverlay} role="dialog" aria-modal="true" aria-labelledby="pooled-pay-confirm-title">
                                            <div className={s.confirmDialog}>
                                                <h3 id="pooled-pay-confirm-title" className={s.confirmTitle}>{t('payoutActions.confirmPooled')}</h3>
                                                <p className={s.confirmBody}>
                                                    {this.state.pooledPayConfirmMsg}
                                                </p>
                                                <div className={s.confirmActions}>
                                                    <button
                                                        type="button"
                                                        className={s.payoutBtn}
                                                        style={{ flex: 1 }}
                                                        onClick={this.executePayPooledShare}
                                                    >
                                                        Confirm — pay via Stripe
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={s.skipBtn}
                                                        style={{ flex: 1 }}
                                                        onClick={() => this.setState({ pooledPayConfirmMsg: null })}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                            
                            {/* Payout / Setup Section */}
                            {!pooledTipsOn && hasLeftoverFailedAfterPaid ? (
                                <div className={s.payoutAction}>
                                    <p className={`${s.payoutNote} ${s.warningNote}`}>
                                        {allFailuresMissingCharge
                                            ? `Retry will not fix ${transferSummary.failedCount} leftover transfer(s) without a Stripe charge. Pay outside Stripe if needed, then use Mark as Paid (Manual) to clear those rows.`
                                            : `Shift tips are paid. ${transferSummary.failedCount} leftover failed transfer(s) remain — clear them with Mark as Paid (Manual) if they are not recoverable.`}
                                    </p>
                                    <button
                                        className={s.skipBtn}
                                        onClick={() => this.handleMarkAllAsPaidManual(transferSummary.failedAmount)}
                                        disabled={anyActionInFlight}
                                        style={{ width: '100%', marginTop: 10 }}
                                        title="Clear leftover failed transfer rows after paying outside Stripe if needed"
                                    >
                                        {isSkippingTransfers ? (
                                            <><Loader size={16} /> {t('payoutActions.markingPaid')}</>
                                            ) : (
                                            <><i className="zmdi zmdi-check-all"></i> {t('payoutActions.markPaid')}</>
                                        )}
                                    </button>
                                </div>
                            ) : null}

                            {!pooledTipsOn && totalOwed > 0 && displayTipPayoutStatus !== 'paid' ? (
                                <div className={s.payoutAction}>
                                    {this.hasStripeSetup() ? (
                                        <>
                                            {payoutActionMessage ? (
                                                <p className={`${s.payoutNote} ${showRetryFailedForDisplay || showMerchantFundedActions ? s.warningNote : ''}`}>
                                                    {payoutActionMessage}
                                                </p>
                                            ) : null}
                                            {showPendingNotice && !showMerchantFundedActions ? (
                                                <p className={s.payoutMeta}>Some transfers are still pending for this work period.</p>
                                            ) : null}
                                            {allFailuresMissingCharge && transferSummary.failedCount > 0 && !showRetryFailedForDisplay ? (
                                                <p className={`${s.payoutNote} ${s.warningNote}`}>
                                                    {transferSummary.failedCount} transfer(s) totaling {this.formatCurrency(transferSummary.failedAmount)} failed with no Stripe charge — Retry will not succeed. Pay outside Stripe if needed, then Mark as Paid (Manual).
                                                </p>
                                            ) : null}
                                            {showRetryFailedForDisplay ? (
                                                <button
                                                    className={s.retryFailedBtn}
                                                    onClick={this.handleRetryFailedTransfers}
                                                    disabled={anyActionInFlight}
                                                >
                                                    {isRetryingFailedTransfers ? (
                                                        <><Loader size={16} /> Retrying Failed Transfers...</>
                                                    ) : (
                                                        <><i className="zmdi zmdi-refresh"></i> {t('payoutActions.retryFailed')}</>
                                                    )}
                                                </button>
                                            ) : (
                                                !showMerchantFundedActions && !this.isTipPayoutDisabled() && (
                                                    <>
                                                        <button
                                                            className={`${s.payoutBtn} ${canGuaranteedPayout ? '' : s.payoutBtnBlocked}`}
                                                            onClick={this.handleManualPayout}
                                                            disabled={anyActionInFlight || !canGuaranteedPayout}
                                                            title={canGuaranteedPayout
                                                                ? 'Pay tips from platform Available balance'
                                                                : (payoutReadiness.reasons[0] || 'Payout not ready')}
                                                        >
                                                            {isTriggeringPayout ? (
                                                                <><Loader size={16} /> Processing...</>
                                                            ) : (
                                                                <><i className="zmdi zmdi-money"></i> {t('payoutActions.payOutTips')} ({this.formatCurrency(remainingPayoutAmount)})</>
                                                            )}
                                                        </button>
                                                        {!canGuaranteedPayout ? (
                                                            <ul className={s.payoutGateReasons}>
                                                                {payoutReadiness.reasons.map((reason) => (
                                                                    <li key={reason}>{reason}</li>
                                                                ))}
                                                            </ul>
                                                        ) : (
                                                            <p className={s.payoutMeta}>
                                                                {this.isMerchantTipRoutingMode()
                                                                    ? 'Available covers this employee. Merchant tip routing will reclaim each sale’s tip from the merchant account before paying the employee. Stripe may still hold a sale if that reclaim or balance has not cleared yet.'
                                                                    : 'Available covers this employee. Stripe may still hold individual sales until retained tip fees clear on those charges.'}
                                                            </p>
                                                        )}
                                                    </>
                                                )
                                            )}

                                            {/* Proactive Mark as Paid (Manual) — always available when there
                                                are unpaid earnings. Useful when tips were captured before
                                                Auto-Transfer mode was enabled and the funds live on the
                                                merchant's Stripe account, or when the employee was already
                                                paid externally (cash, check, payroll). */}
                                            {!showMerchantFundedActions ? (
                                                <button
                                                    type="button"
                                                    className={s.skipBtn}
                                                    onClick={() => this.handleMarkAllAsPaidManual(remainingPayoutAmount)}
                                                    disabled={anyActionInFlight}
                                                    style={{ width: '100%', marginTop: 10 }}
                                                    title="Use if the employee was already paid by other means (cash, check, payroll)"
                                                >
                                                    {isSkippingTransfers ? (
                                            <><Loader size={16} /> {t('payoutActions.markingPaid')}</>
                                            ) : (
                                            <><i className="zmdi zmdi-check-all"></i> {t('payoutActions.markPaid')}</>
                                                    )}
                                                </button>
                                            ) : null}

                                            {showMerchantFundedActions ? (
                                                <div className={s.merchantFundedSection}>
                                                    <p className={s.merchantFundedTitle}>
                                                        <i className="zmdi zmdi-alert-triangle"></i> Tips routed to merchant
                                                    </p>
                                                    <p className={s.merchantFundedBody}>
                                                        {merchantFundedTransferIds.length} transfer{merchantFundedTransferIds.length === 1 ? '' : 's'} totalling {this.formatCurrency(merchantFundedAmount)} were
                                                        deposited into the merchant's Stripe account at the time of sale, so the platform cannot pay them out directly.
                                                        Choose one of the actions below.
                                                    </p>
                                                    <div className={s.merchantFundedActions}>
                                                        <button
                                                            className={s.skipBtn}
                                                            onClick={() => this.handleSkipMerchantFunded(merchantFundedTransferIds, merchantFundedAmount)}
                                                            disabled={anyActionInFlight}
                                                            title="Use if the employee has already been paid by other means"
                                                        >
                                                            {isSkippingTransfers ? (
                                                                <><Loader size={16} /> Marking as Paid...</>
                                                            ) : (
                                                                <><i className="zmdi zmdi-check"></i> {t('payoutActions.markPaidSkip')}</>
                                                            )}
                                                        </button>
                                                        <button
                                                            className={s.reclaimBtn}
                                                            onClick={() => this.handleReclaimToPlatform(merchantFundedTransferIds, merchantFundedAmount)}
                                                            disabled={anyActionInFlight}
                                                            title="Reverse the merchant transfer back to the platform balance (does not pay employee yet)"
                                                        >
                                                            {isReclaimingToPlatform ? (
                                                                <><Loader size={16} /> Reclaiming to Platform...</>
                                                            ) : (
                                                                <><i className="zmdi zmdi-undo"></i> Reclaim to Platform ({this.formatCurrency(merchantFundedAmount)})</>
                                                            )}
                                                        </button>
                                                        <button
                                                            className={s.reclaimBtn}
                                                            onClick={() => this.handleReclaimAndTransfer(merchantFundedTransferIds, merchantFundedAmount)}
                                                            disabled={anyActionInFlight}
                                                            title="Reverse the merchant transfer and pay the employee via Stripe"
                                                        >
                                                            {isReclaimingTransfers ? (
                                                                <><Loader size={16} /> Reclaiming &amp; Transferring...</>
                                                            ) : (
                                                                <><i className="zmdi zmdi-swap"></i> Reclaim &amp; Transfer ({this.formatCurrency(merchantFundedAmount)})</>
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : null}

                                            {showHeldSection ? (
                                                <div className={s.heldSection}>
                                                    <p className={s.heldTitle}>
                                                        <i className="zmdi zmdi-time"></i> Waiting for Funds to Clear
                                                    </p>
                                                    <p className={s.heldBody}>
                                                        {heldCount} transfer{heldCount === 1 ? '' : 's'}{heldAmount > 0 ? ` totalling ${this.formatCurrency(heldAmount)}` : ''} are
                                                        currently marked as Waiting for Funds to Clear. Stripe still needs the related funds to finish clearing
                                                        before the employee payout can complete. If this hold came from the merchant side, automatic merchant bank
                                                        payouts may already be paused to protect the tip funds. These transfers will retry automatically once the
                                                        funds clear. You can also try now if they have already cleared.
                                                    </p>
                                                    <div className={s.heldActions}>
                                                        <button
                                                            className={s.heldRetryBtn}
                                                            onClick={this.handleProcessHeldReversals}
                                                            disabled={anyActionInFlight}
                                                            title="Retry transfers that are waiting for funds to clear"
                                                        >
                                                            {isProcessingHeld ? (
                                                                <><Loader size={16} /> Trying...</>
                                                            ) : (
                                                                <><i className="zmdi zmdi-refresh"></i> {t('payoutActions.tryNow')}</>
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : null}
                                        </>
                                    ) : (
                                        this.isPayoutStatusUnknownForCurrentUser() ? (
                                            <div className={s.setupSection}>
                                                <div className={s.setupLabel}>
                                                    <Loader size={14} /> Checking payout setup…
                                                </div>
                                            </div>
                                        ) : this.isTipPayoutDisabled() ? (
                                        <div className={s.setupSection}>
                                            <p className={s.setupLabel}>
                                                <i className="zmdi zmdi-alert-circle"></i>
                                                Tip payouts are disabled in Store Settings
                                            </p>
                                            <p className={s.setupWarning}>
                                                Employee payment setup links are unavailable while tip payouts are disabled for this store.
                                                You can still mark tips as paid if the employee was paid outside Stripe.
                                            </p>
                                            <button
                                                type="button"
                                                className={s.skipBtn}
                                                onClick={() => this.handleMarkAllAsPaidManual(remainingPayoutAmount)}
                                                disabled={anyActionInFlight}
                                                style={{ width: '100%', marginTop: 12 }}
                                                title="Use if the employee was already paid by other means (cash, check, payroll)"
                                            >
                                                {isSkippingTransfers ? (
                                            <><Loader size={16} /> {t('payoutActions.markingPaid')}</>
                                            ) : (
                                            <><i className="zmdi zmdi-check-all"></i> {t('payoutActions.markPaid')}</>
                                                )}
                                            </button>
                                        </div>
                                        ) : (
                                        <div className={s.setupSection}>
                                            <p className={s.setupLabel}>
                                                <i className="zmdi zmdi-alert-circle"></i>
                                                Send Employee Payment Setup Link
                                            </p>
                                            <p className={s.setupWarning} style={{ marginBottom: 12 }}>
                                                This employee is not eligible for Stripe tip payouts yet.
                                                You can still mark tips as paid if they were paid outside Stripe
                                                (cash, check, payroll).
                                            </p>
                                            <div className={s.setupButtons}>
                                                <button
                                                    className={s.setupBtnEmail}
                                                    onClick={this.handleSendEmail}
                                                    disabled={!employee?.Email || this.props.isConnecting}
                                                    title={employee?.Email ? `Send to ${employee.Email}` : 'No email on file'}
                                                >
                                                    {this.props.isConnecting ? <Loader size={14} /> : <><i className="zmdi zmdi-email"></i> {t('payoutActions.emailSetup')}</>}
                                                </button>
                                                <button
                                                    className={s.setupBtnSms}
                                                    onClick={this.handleSendSms}
                                                    disabled={!employee?.Telephone || this.props.isConnecting}
                                                    title={employee?.Telephone ? `Send to ${employee.Telephone}` : 'No phone on file'}
                                                >
                                                    {this.props.isConnecting ? <Loader size={14} /> : <><i className="zmdi zmdi-smartphone"></i> {t('payoutActions.textSetup')}</>}
                                                </button>
                                            </div>
                                            {!employee?.Email && !employee?.Telephone && (
                                                <p className={s.setupWarning}>
                                                    Add an email or phone number to this employee first.
                                                </p>
                                            )}
                                            <button
                                                type="button"
                                                className={s.skipBtn}
                                                onClick={() => this.handleMarkAllAsPaidManual(remainingPayoutAmount)}
                                                disabled={anyActionInFlight}
                                                style={{ width: '100%', marginTop: 12 }}
                                                title="Use if the employee was already paid by other means (cash, check, payroll)"
                                            >
                                                {isSkippingTransfers ? (
                                            <><Loader size={16} /> {t('payoutActions.markingPaid')}</>
                                            ) : (
                                            <><i className="zmdi zmdi-check-all"></i> {t('payoutActions.markPaid')}</>
                                                )}
                                            </button>
                                        </div>
                                        )
                                    )}
                                </div>
                            ) : null}
                        </div>
                    </Widget>
                </div>
            </div>
        );
    }
}

function mapStateToProps(state, ownProps) {
    const selectedEmployee = state.users.selectedEmployee || null;
    // Fallback: look up employee from the full users list by URL param
    const userId = parseInt(ownProps.match?.params?.userId);
    const userFromList = userId
        ? (state.users.data || []).find(u => u.UserId === userId) || null
        : null;
    // The work period snapshot we navigate in with (location.state) is frozen
    // at navigation time. After actions like Mark as Paid (Manual) /
    // Reclaim & Transfer, the reducer updates the matching row in
    // state.users.employeeSales (e.g. UPDATE_SALE_PAYOUT_STATUS flips
    // TipPayoutStatus to 'paid'). Surface the latest copy here so render()
    // can prefer the Redux row over the stale location-state snapshot.
    //
    // Match on WorkperiodId, not on the synthetic row Id, because different
    // entry points (Users tab vs Sales report) build the snapshot from
    // different APIs that don't share an id format. The URL workperiodId
    // param is the one identifier guaranteed to match the
    // GET /api/UserAdmin/sales row's WorkPeriodId.
    const routeWpId = parseInt(ownProps.match?.params?.workperiodId) || null;
    const liveWorkperiod = routeWpId
        ? (state.users.employeeSales || []).find(s => {
            const wpId = s.WorkPeriodId || s.workPeriodId || s.WorkperiodId || s.workperiodId;
            return Number(wpId) === Number(routeWpId);
        }) || null
        : null;
    return {
        selectedEmployee: selectedEmployee || userFromList,
        liveWorkperiod,
        triggeringSaleId: state.users.triggeringSaleId || null,
        retryingFailedWorkPeriodId: state.users.retryingFailedWorkPeriodId || null,
        skippingTransfersWorkPeriodId: state.users.skippingTransfersWorkPeriodId || null,
        reclaimingTransfersWorkPeriodId: state.users.reclaimingTransfersWorkPeriodId || null,
        reclaimingToPlatformWorkPeriodId: state.users.reclaimingToPlatformWorkPeriodId || null,
        processingHeldWorkPeriodId: state.users.processingHeldWorkPeriodId || null,
        payoutAttemptResultsByWorkPeriodId: state.users.payoutAttemptResultsByWorkPeriodId || {},
        payoutStatus: state.users.payoutStatus || null,
        payoutStatusLoading: state.users.payoutStatusLoading || false,
        payoutStatusLoadingUserId: state.users.payoutStatusLoadingUserId || null,
        merchantId: state.auth.merchant?.MerchantId || 0,
        tipPayoutRoutingMode: state.settings.storeDetails?.TipPayoutRoutingMode || state.auth.merchant?.TipPayoutRoutingMode || 3,
        storeSettingsMerchantId: state.settings.storeDetails?.MerchantId || 0,
        isConnecting: state.stripe.isConnecting || false,
        employeeSalesLoading: state.users.employeeSalesLoading || false,
        tipFundingSummary: state.users.tipFundingSummary || null,
        tipFundingSummaryLoading: state.users.tipFundingSummaryLoading || false,
        tipFundingSummaryError: state.users.tipFundingSummaryError || '',
        identityRolesLoaded: state.auth.identityRolesLoaded || false,
        isRebelityAdmin: state.auth.isRebelityAdmin || false,
        merchant: state.auth.merchant || null,
    };
}

export default withRouter(connect(mapStateToProps)(withTranslation(WorkperiodDetails)));
